import { useContent } from '@altify/utils';
import * as PopoverPrimitive from '@rn-primitives/popover';
import Fuse from 'fuse.js';
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { cn } from '../../lib/utils';
import { TextInput } from '../TextInput/TextInput';
import { Typography } from '../Typography/Typography';
import { content } from './AutocompleteInput.content';

export type AutocompleteOption = {
  value: string | null;
  label: string;
};

export interface AutocompleteInputProps {
  items: AutocompleteOption[];
  value?: AutocompleteOption;
  label?: string;
  error?: string;
  resultLimit?: number;
  maxHeight?: number;
  minSearchLength?: number;
  acceptOther?: boolean;
  onSelect?: (value: AutocompleteOption) => void;
  placeholder?: string;
}

export function AutocompleteInput({
  items,
  label,
  value,
  error,
  onSelect,
  resultLimit = 50,
  maxHeight = 250,
  minSearchLength = 1,
  acceptOther = false,
  placeholder,
}: AutocompleteInputProps) {
  const localizedContent = useContent(content);
  const [query, setQuery] = useState(value?.label ?? '');
  const safeInsets = useContext(SafeAreaInsetsContext);
  const insets = {
    top: (safeInsets?.top ?? 0) + 24,
    bottom: (safeInsets?.bottom ?? 0) + 24,
    left: (safeInsets?.left ?? 0) + 12,
    right: (safeInsets?.right ?? 0) + 12,
  };

  // Since the content of the dropdown is rendered inside a portal, there is no easier way to make it the same size as the input
  const [triggerWidth, setTriggerWidth] = useState(0);
  const triggerRef = useRef<PopoverPrimitive.TriggerRef>(null);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ['label'],
        shouldSort: true,
        ignoreLocation: true,
        // Lower means the search is more strict
        threshold: 0.1,
      }),
    [items],
  );
  const filteredItems = useMemo(() => {
    var filtered = query
      ? fuse.search(query).map(result => result.item)
      : items;
    var limited = filtered.slice(0, resultLimit);

    if (query && acceptOther) {
      limited.push({
        label: `${query} (${localizedContent.other})`,
        value: null,
      });
    }

    return limited;
  }, [query, items, resultLimit]);

  function handleSelect(value: AutocompleteOption) {
    triggerRef.current?.close();
    var label = value?.value == null ? query : (value?.label ?? '');

    setQuery(label);
    onSelect?.({ value: value.value, label: label });
  }

  function handleInputChange(value: string) {
    if (value.length >= minSearchLength) {
      triggerRef.current?.open();
    } else {
      triggerRef.current?.close();
    }

    setQuery(value);
  }

  function handleBlur() {
    // Reset value if field is emptied
    if (!query) {
      onSelect?.({ value: null, label: '' });
      return;
    }

    // Use item if query matches exactly (ignoring case)
    var similarItem = items.find(
      item => item.label.toUpperCase() === query.trim().toUpperCase(),
    );
    if (similarItem) {
      onSelect?.({ value: similarItem.value, label: similarItem.label });
      setQuery(similarItem.label);
      return;
    }

    // If acceptOther is true we just use the query as the value even if it doesn't match any item
    if (acceptOther) {
      onSelect?.({ value: null, label: query });
      return;
    }

    // Otherwise reject change and reset the input string
    resetQuery();
  }

  function resetQuery() {
    setQuery(value?.label ?? '');
  }

  useEffect(() => {
    resetQuery();
  }, [value]);

  return (
    <View className="w-full">
      {label && (
        <Typography size="small" className="text-neutral-300 mb-1">
          {label}
        </Typography>
      )}
      <PopoverPrimitive.Root
        onLayout={ev => setTriggerWidth(ev.nativeEvent.layout.width)}
        onOpenChange={isOpen => {
          if (!isOpen) {
            handleBlur();
          }
        }}>
        <TextInput
          //onFocus={() => triggerRef.current?.open()}
          //onBlur={() => triggerRef.current?.close()}
          value={query}
          onValueChanged={handleInputChange}
          placeholder={placeholder ?? label ?? ''}
        />
        <PopoverPrimitive.Trigger
          ref={triggerRef}
          disabled></PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Overlay style={StyleSheet.absoluteFill}>
            <PopoverPrimitive.Content
              avoidCollisions={true}
              insets={insets}
              className="mt-2 shadow-lg rounded-lg"
              style={{
                width: triggerWidth,
                elevation: 2,
              }}
              // https://ariakit.org/examples/combobox-radix
              // https://ariakit.org/examples/combobox-radix-select
              onOpenAutoFocus={ev => ev.preventDefault()}>
              <View className="bg-neutral-500 rounded-lg overflow-hidden">
                <ScrollView style={{ maxHeight }}>
                  {filteredItems.map((item, index) => (
                    <Fragment key={item.value}>
                      <Pressable
                        onPress={() => handleSelect(item)}
                        className={cn(
                          'py-3 px-2 hover:bg-primary hover:outline-none hover:border-none focus:outline-none focus:border-none focus:bg-primary active:bg-primary-dark',
                          {
                            'border-2 border-error focus:border-error text-error':
                              error,
                          },
                        )}>
                        <Typography>{item.label}</Typography>
                      </Pressable>
                      {index < filteredItems.length - 1 && (
                        <View className="h-px bg-white opacity-20" />
                      )}
                    </Fragment>
                  ))}
                </ScrollView>
              </View>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Overlay>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      {error && (
        <Typography size="small" className="text-error mt-2">
          {error}
        </Typography>
      )}
    </View>
  );
}
